import { keepPreviousData, queryOptions, useQuery } from '@tanstack/react-query';
import { api } from '@/api';
import {
  AssignmentCandidateParams,
  AssignmentCSCandidatesParams,
  EmployeesReservationsParams,
  ReliefAssignmentCSCandidateParams,
} from '@/api/apis/resources';
import { resourcesQueryKeys } from '@/queries/resource/resources.keys';
import { Uuid } from '@/api/types';
import { OrganType } from '@/api/apis/case';

export const resourcesQueries = {
  employeesReservations: (params: EmployeesReservationsParams) =>
    queryOptions({
      queryKey: resourcesQueryKeys.getEmployeesReservations(params),
      queryFn: () => api.resource.getEmployeesReservations(params),
    }),
  csAssignmentCandidates: (params: AssignmentCSCandidatesParams) =>
    queryOptions({
      queryKey: resourcesQueryKeys.getCSCandidates(params),
      queryFn: ({ signal }) => api.resource.getCsCandidatesForAssignment(params, signal),
      refetchOnWindowFocus: false,
    }),
  reliefCsAssignmentCandidates: (params: ReliefAssignmentCSCandidateParams) =>
    queryOptions({
      queryKey: resourcesQueryKeys.getReliefCsCandidates(params),
      queryFn: ({ signal }) => api.resource.getReliefCsCandidatesForAssignment(params, signal),
      refetchOnWindowFocus: false,
    }),
  surgeonAssignmentCandidates: (params: AssignmentCandidateParams) =>
    queryOptions({
      queryKey: resourcesQueryKeys.getRSCandidates(params),
      queryFn: ({ signal }) => api.resource.getSurgeonCandidatesForAssignment(params, signal),
      refetchOnWindowFocus: false,
    }),
  contractorSurgeonAssignmentCandidates: (params: AssignmentCandidateParams) =>
    queryOptions({
      queryKey: resourcesQueryKeys.getCRSCandidates(params),
      queryFn: ({ signal }) => api.resource.getContractorSurgeonCandidatesForAssignment(params, signal),
      refetchOnWindowFocus: false,
    }),
  coordinatorAssignmentCandidates: (assignedEmployeeIds: Uuid[]) =>
    queryOptions({
      queryKey: resourcesQueryKeys.getCoordinatorCandidates(assignedEmployeeIds),
      queryFn: ({ signal }) => api.resource.getCoordinatorCandidatesForAssignment(assignedEmployeeIds, signal),
      refetchOnWindowFocus: false,
    }),
  omsAssignmentCandidates: (assignedEmployeeIds: Uuid[]) =>
    queryOptions({
      queryKey: resourcesQueryKeys.getOMSCandidates(assignedEmployeeIds),
      queryFn: ({ signal }) => api.resource.getOMSCandidatesForAssignment(assignedEmployeeIds, signal),
      refetchOnWindowFocus: false,
    }),
  clinicalLeadAssignmentCandidates: (assignedEmployeeIds: Uuid[], organ: OrganType) =>
    queryOptions({
      queryKey: resourcesQueryKeys.getClinicalLeadCandidates(assignedEmployeeIds, organ),
      queryFn: ({ signal }) => api.resource.getClinicalLeadsForAssignment(assignedEmployeeIds, organ, signal),
      refetchOnWindowFocus: false,
    }),
};

export const useEmployeesReservations = (params: EmployeesReservationsParams, enabled = false) => {
  return useQuery({
    ...resourcesQueries.employeesReservations(params),
    placeholderData: keepPreviousData,
    enabled,
  });
};

export const useClinicalSpecialistsForAssignment = (params: AssignmentCSCandidatesParams, enabled: boolean = true) => {
  return useQuery({
    ...resourcesQueries.csAssignmentCandidates(params),
    enabled,
  });
};

export const useReliefSpecialistsForAssignment = (
  params: ReliefAssignmentCSCandidateParams,
  enabled: boolean = true,
) => {
  return useQuery({
    ...resourcesQueries.reliefCsAssignmentCandidates(params),
    enabled,
  });
};

export const useSurgeonsForAssignment = (params: AssignmentCandidateParams) => {
  return useQuery({
    ...resourcesQueries.surgeonAssignmentCandidates(params),
  });
};

export const useContractorSurgeonsForAssignment = (params: AssignmentCandidateParams) => {
  return useQuery({
    ...resourcesQueries.contractorSurgeonAssignmentCandidates(params),
  });
};

export const useCoordinatorsForAssignment = (assignedEmployeeIds: Uuid[]) => {
  return useQuery({
    ...resourcesQueries.coordinatorAssignmentCandidates(assignedEmployeeIds),
  });
};

export const useOMSForAssignment = (assignedEmployeeIds: Uuid[]) => {
  return useQuery({
    ...resourcesQueries.omsAssignmentCandidates(assignedEmployeeIds),
  });
};

export const useClinicalLeadsForAssignment = (assignedEmployeeIds: Uuid[], organ: OrganType) => {
  return useQuery({
    ...resourcesQueries.clinicalLeadAssignmentCandidates(assignedEmployeeIds, organ),
  });
};
